<script setup lang="ts">
import { PropType, reactive, ref, computed } from 'vue';
import { ModalToolbar } from 'md-editor-v3';
import type { Insert } from 'md-editor-v3';
import { useStore } from 'vuex';
import { ShiftCMSState } from '@/store';
import { ContentStoreState } from '@/stores/content.store';
import { Initiative } from '@/models/initiative.model';

const data = reactive({
  modalVisible: false,
  modalFullscreen: false,
});

const store = useStore<ShiftCMSState & { cns: ContentStoreState }>();

const initiatives = computed<Initiative[]>(() => store.state.cns.initiatives);

const props = defineProps({
  /**
   * `insert` will be automatically injected into the component by the editor
   */
  insert: {
    type: Function as PropType<Insert>,
    default: () => {
      //
    },
  },
});

const handler = (target: string) => {
  props.insert((selectedText) => {
    /**
     * targetValue    Content to be inserted
     * select         Automatically select content, default: true
     * deviationStart Start position of the selected content, default: 0
     * deviationEnd   End position of the selected content, default: 0
     */
    return {
      targetValue: `[${selectedText || 'description'}](${target})`,
      select: true,
      deviationStart: 0,
      deviationEnd: 0,
    };
  });

  data.modalVisible = false;
};

const chooseLink = () => {
  if (linkUrl.value !== '') {
    handler(linkUrl.value);
  }
};

const linkUrl = ref<string>('');

const tab = ref<string>('link');
</script>
<template>
  <ModalToolbar
    :visible="data.modalVisible"
    :isFullscreen="data.modalFullscreen"
    title="media"
    modalTitle="Insert Link"
    width="600px"
    height="800px"
    @onClick="data.modalVisible = true"
    @onClose="data.modalVisible = false"
    @onAdjust="data.modalFullscreen = !data.modalFullscreen"
  >
    <v-tabs v-model="tab" fixed-tabs density="compact">
      <v-tab value="link">Link</v-tab>
      <v-tab value="initiative">Initiative Link</v-tab>
    </v-tabs>
    <v-tabs-window v-model="tab">
      <v-tabs-window-item value="link">
        <v-container>
          <h3>Insert a link into the text</h3>
          <p>Provide a url</p>
          <v-text-field
            label="URL"
            v-model="linkUrl"
            placeholder="https://"
          ></v-text-field>

          <v-row>
            <v-col col="2"></v-col>
            <v-col>
              <v-btn
                class="mt-2"
                type="reset"
                block
                @click="data.modalVisible = false"
                >Cancel</v-btn
              >
            </v-col>
            <v-col>
              <v-btn class="mt-2" block type="button" @click="chooseLink"
                >Save</v-btn
              >
            </v-col>
            <v-col col="2"></v-col>
          </v-row>
        </v-container>
      </v-tabs-window-item>
      <v-tabs-window-item value="initiative">
        <v-container>
          <h3>Link to an initiative</h3>
          <p>Choose the initiative</p>

          <v-select
            v-model="linkUrl"
            label="Initiative"
            :menuProps="{ attach: true }"
            hide-details="auto"
            :items="initiatives.map((q) => ({ title: q.title, value: q }))"
            ><template v-slot:item="{ props }">
              <v-list-item v-bind="props">
                <v-list-item-title></v-list-item-title>
              </v-list-item> </template
          ></v-select>

          <v-row>
            <v-col col="2"></v-col>
            <v-col>
              <v-btn
                class="mt-2"
                type="reset"
                block
                @click="data.modalVisible = false"
                >Cancel</v-btn
              >
            </v-col>
            <v-col>
              <v-btn class="mt-2" block type="button" @click="chooseLink"
                >Save</v-btn
              >
            </v-col>
            <v-col col="2"></v-col>
          </v-row>
        </v-container>
      </v-tabs-window-item>
    </v-tabs-window>
    <template #trigger>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="lucide lucide-link-icon md-editor-icon"
      >
        <path
          d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
        ></path>
        <path
          d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
        ></path>
      </svg>
    </template>
  </ModalToolbar>
</template>
<style scoped>
.v-field[aria-expanded='false'] {
  border: solid 3px red;
}

.v-field[aria-expanded='false'] .v-overlay {
  height: 300px;
}
.v-field[aria-expanded='true'] .v-overlay {
  height: 500px;
}
</style>
